import { CANONICAL_PRODUCTION_ORIGIN } from "../auth/origin";

/**
 * White-label continue contract (broker/IMO of record).
 *
 * Evergreen Legacy / Capital DFC is the only broker/IMO that may appear in
 * the browser URL, Evergreen-owned CTAs, emails we compose, and page chrome.
 * Competing broker / IMO / quoting-platform hosts (and their apply links)
 * must never be rendered. Stored `apply_link` / `external_case_url` values
 * stay server-side so `/q/[id]` can mint an in-page embed.
 *
 * Issuing-carrier disclosure is allowed and required: carrier name, product,
 * premium, AM Best, and carrier chrome inside the embedded widget (e.g.
 * Corebridge) are not competing brokers. This helper only strips broker-
 * platform apply hosts — not carrier names or carrier marketing sites.
 */

const VENDOR_APPLY_HOST_SUFFIXES = [
  "back9ins.com",
  "quoteandapply.io",
] as const;

export function isVendorApplyHost(
  hostname: string | null | undefined
): boolean {
  if (!hostname) return false;
  const host = hostname.trim().toLowerCase().replace(/\.$/, "");
  return VENDOR_APPLY_HOST_SUFFIXES.some(
    (suffix) => host === suffix || host.endsWith(`.${suffix}`)
  );
}

export function isVendorApplyUrl(value: string | null | undefined): boolean {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  try {
    const parsed = new URL(trimmed, `${CANONICAL_PRODUCTION_ORIGIN}/`);
    if (isVendorApplyHost(parsed.hostname)) return true;
  } catch {
    // Fall through to the host-substring check for malformed values.
  }
  return /(?:^|[/.])(?:back9ins\.com|quoteandapply\.io)(?:[:/?#]|$)/i.test(
    trimmed
  );
}

/** Same-origin resume path. Never a competing broker/IMO apply host. */
export function brandedContinueHref(input: {
  applicationId?: string | null;
}): string | null {
  const id = input.applicationId?.trim();
  return id ? `/q/${id}` : null;
}

export function publicSiteOrigin(): string {
  const raw = process.env.AUTH_URL || CANONICAL_PRODUCTION_ORIGIN;
  return raw.replace(/\/+$/, "");
}

/** Absolute branded resume URL for CRM / email / SMS we compose. */
export function brandedContinueAbsoluteUrl(applicationId: string): string {
  const path = brandedContinueHref({ applicationId });
  if (!path) {
    return `${publicSiteOrigin()}/account/login`;
  }
  return `${publicSiteOrigin()}${path}`;
}

/**
 * Drop `applyLink` so a public JSON / server-action payload cannot leak a
 * competing broker/IMO apply host to the browser.
 */
export function stripVendorApplyFields<T extends object>(
  value: T
): Omit<T, "applyLink"> {
  if (!("applyLink" in value)) return value as Omit<T, "applyLink">;
  const rest = { ...value } as T & { applyLink?: unknown };
  delete rest.applyLink;
  return rest;
}

/**
 * Last-line href sanitizer for Evergreen chrome. Competing broker/IMO
 * apply hosts become null; absolute Evergreen / preview URLs collapse to
 * a same-origin path. Continue CTAs stay on evergreenlifeinsurance.com —
 * this does not hide carrier names in copy.
 */
export function publicHrefOrNull(
  href: string | null | undefined
): string | null {
  if (!href) return null;
  const trimmed = href.trim();
  if (!trimmed) return null;
  if (isVendorApplyUrl(trimmed)) return null;
  if (trimmed.startsWith("/") && !trimmed.startsWith("//")) return trimmed;
  try {
    const url = new URL(trimmed);
    if (isVendorApplyHost(url.hostname)) return null;
    const host = url.hostname.toLowerCase();
    const allowed =
      host === "evergreenlifeinsurance.com" ||
      host === "www.evergreenlifeinsurance.com" ||
      host === "localhost" ||
      host.endsWith(".azurestaticapps.net");
    if (!allowed) return null;
    return `${url.pathname}${url.search}${url.hash}` || "/";
  } catch {
    return null;
  }
}

/** Rewrite a composed consumer URL if a broker-platform apply host slipped in. */
export function sanitizeComposedConsumerUrl(url: string): string {
  if (!isVendorApplyUrl(url)) return url;
  return `${CANONICAL_PRODUCTION_ORIGIN}/account/login`;
}
